import type { Image3D } from '~/domain/patient/image3d';

/**
 * The HU → relative density conversion MVision Image+ applies to its
 * synthetic CTs (Image+ 1.1.0). This is a product constant of the
 * generator, NOT a scanner calibration: it must never be applied to a
 * real CT, whose own calibration differs. Gate every use with
 * {@link isImagePlusSct}.
 *
 * Transcribed from imageplus_hu_density_1.1.0.json (descriptive
 * source, kept outside the repo); nothing in this repository re-checks
 * the transcription against it. Nine points, strictly monotonic in HU
 * and non-decreasing in both
 * densities — pinned by test, since a non-monotonic table would make
 * the interpolation ambiguous.
 */
const IMAGE_PLUS_HU_DENSITY_POINTS: ReadonlyArray<{
    readonly hu: number;
    readonly relMass: number;
    readonly relElectron: number;
}> = [
    { hu: -1000, relMass: 0, relElectron: 0 },
    { hu: -800, relMass: 0.205, relElectron: 0.2 },
    { hu: -70, relMass: 0.96, relElectron: 0.949 },
    { hu: -40, relMass: 0.99, relElectron: 0.976 },
    { hu: 0, relMass: 1, relElectron: 1 },
    { hu: 40, relMass: 1.04, relElectron: 1.023 },
    { hu: 210, relMass: 1.16, relElectron: 1.117 },
    { hu: 820, relMass: 1.53, relElectron: 1.456 },
    { hu: 1750, relMass: 2.15, relElectron: 1.976 },
];

/**
 * Relative electron and mass densities (water = 1). Unitless: no unit
 * suffix in a name or display, and NOT subject to the mm→cm rule.
 */
export interface SctDensity {
    relElectron: number;
    relMass: number;
}

/**
 * The density pair Image+ associates with `hu`, linearly interpolated
 * between the nine table points, or undefined outside the table (below
 * −1000 or above 1750 HU).
 *
 * Undefined rather than the end row: by design an Image+ sCT contains no
 * HU outside the table's range, so out-of-range input means the image was
 * post-processed after generation or the tags sit on a series that is not
 * genuine Image+ output. The product characterises no density there, and
 * reporting the end row would present a clamped value as a measured one —
 * a 2500 HU voxel reading the same as a genuine 1750 HU one. Nor does it
 * extrapolate the curve into territory the product does not characterise.
 * The HU the readout shows beside it already says why there is no density.
 */
export function sctDensityAtHu(hu: number): SctDensity | undefined {
    const points = IMAGE_PLUS_HU_DENSITY_POINTS;
    if (hu < points[0].hu || hu > points[points.length - 1].hu) {
        return undefined;
    }
    for (let i = 1; i < points.length; i++) {
        const upper = points[i];
        if (hu > upper.hu) {
            continue;
        }
        // A tabulated HU returns its row VERBATIM: interpolating with
        // t = 1 reintroduces floating-point error on the very values
        // the table claims exactly.
        if (hu === upper.hu) {
            return { relElectron: upper.relElectron, relMass: upper.relMass };
        }
        const lower = points[i - 1];
        const t = (hu - lower.hu) / (upper.hu - lower.hu);
        return {
            relElectron:
                lower.relElectron + t * (upper.relElectron - lower.relElectron),
            relMass: lower.relMass + t * (upper.relMass - lower.relMass),
        };
    }
    // Unreachable: an HU within the table meets some upper point.
    throw new Error(`sctDensityAtHu: no table interval holds ${hu} HU`);
}

/** Trimmed, case-insensitive tag comparison. Padding and casing vary
 *  between DICOM writers; the WORDS may not. */
function tagMatches(actual: string, expected: string): boolean {
    return actual.trim().toLowerCase() === expected;
}

/**
 * Whether `imageType` (the DICOM Image Type multi-valued attribute) carries
 * the SYNCT value that marks a synthetic CT.
 *
 * Deliberately NOT an exact match against a full Image Type sequence:
 * this app's own rewrite path (`dicomRewriteImage.ts`) writes
 * DERIVED\SECONDARY\<plane>\REFORMATTED on any image it rewrites, so
 * DERIVED, SECONDARY and AXIAL are generic — a plain derived image
 * carries them too — and have no discriminating power here. SYNCT is the
 * only value that actually identifies a synthetic CT. Matching the whole
 * sequence would invite a future "tightening" to exact equality that then
 * breaks on any series whose sequence differs slightly (order, extra
 * values, a different writer).
 */
function hasSynctValue(imageType: readonly string[]): boolean {
    return imageType.some((value) => tagMatches(value, 'synct'));
}

/**
 * Whether `image` is an MVision Image+ synthetic CT — the only series
 * {@link sctDensityAtHu} may be applied to.
 *
 * ALL of Manufacturer, ManufacturerModelName and the ImageType SYNCT value
 * must match. Any one alone is spoofable by coincidence (another vendor's
 * "Image+", a derived image that happens to be tagged SYNCT); the
 * conjunction is what identifies genuine Image+ output. Loosening any
 * single condition is a safety regression — each is pinned by its own
 * test. StationName is deliberately not part of the rule (product
 * decision).
 */
export function isImagePlusSct(image: Image3D): boolean {
    const slice = image.firstSlice;
    return (
        tagMatches(slice.manufacturer, 'mvision ai') &&
        tagMatches(slice.manufacturerModelName, 'image+') &&
        hasSynctValue(slice.imageType)
    );
}
